import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import BlockModal from './BlockModal';
import TierLimitModal from './TierLimitModal';
import { TIER_LIMIT_COPY, TierLimitKey } from '../../lib/tierLimits';

/**
 * BlockDialogProvider — one mount, app-wide, that any surface can fire a
 * blocking dialog from without wiring its own modal state.
 *
 * Generalizes the AssignmentBlockedModal pattern (AVM-019): high-consequence
 * refusals that were easy-to-miss toasts now escalate to a must-acknowledge
 * dialog. Two flavours:
 *   • showBlock(...)   — a data-integrity guardrail ("can't do this, here's why").
 *   • showUpgrade(...) — a tier/feature gate, carrying the one-click upgrade CTA.
 *
 * Low-stakes/transient feedback ("Saved", network blips) stays a toast; this is
 * only for refusals the user must understand. See [[project-blocking-modal-pattern]].
 */

interface BlockArgs {
  title: string;
  subtitle?: string;
  /** The explanation body (paragraphs / nodes). */
  body: ReactNode;
  acknowledgeLabel?: string;
}

interface BlockDialogApi {
  /** Show a data-integrity guardrail dialog (no upgrade path). */
  showBlock: (args: BlockArgs) => void;
  /** Show a tier-limit wall with a one-click route to billing. */
  showUpgrade: (key: TierLimitKey, detail?: string) => void;
}

const BlockDialogContext = createContext<BlockDialogApi | null>(null);

/**
 * Access the global block-dialog API. Safe to call anywhere under the provider;
 * outside it (e.g. a unit test that forgot the wrapper) the calls no-op rather
 * than throw, so a missing provider degrades gracefully instead of crashing.
 */
export function useBlockDialog(): BlockDialogApi {
  return useContext(BlockDialogContext) ?? NOOP_API;
}

const NOOP_API: BlockDialogApi = { showBlock: () => {}, showUpgrade: () => {} };

interface UpgradeState {
  title: string;
  what: string;
  unlocks: string;
  detail?: string;
}

export default function BlockDialogProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [block, setBlock] = useState<BlockArgs | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradeState | null>(null);

  const showBlock = useCallback((args: BlockArgs) => setBlock(args), []);

  const showUpgrade = useCallback((key: TierLimitKey, detail?: string) => {
    const copy = TIER_LIMIT_COPY[key];
    setUpgrade({ ...copy, detail });
  }, []);

  const goToBilling = useCallback(() => {
    setUpgrade(null);
    navigate('/admin/billing');
  }, [navigate]);

  return (
    <BlockDialogContext.Provider value={{ showBlock, showUpgrade }}>
      {children}
      <BlockModal
        open={block !== null}
        title={block?.title ?? ''}
        subtitle={block?.subtitle}
        acknowledgeLabel={block?.acknowledgeLabel}
        onClose={() => setBlock(null)}
      >
        {block?.body}
      </BlockModal>
      <TierLimitModal
        open={upgrade !== null}
        title={upgrade?.title ?? ''}
        what={upgrade?.what ?? ''}
        unlocks={upgrade?.unlocks ?? ''}
        detail={upgrade?.detail}
        onUpgrade={goToBilling}
        onClose={() => setUpgrade(null)}
      />
    </BlockDialogContext.Provider>
  );
}
